import type { ReactNode } from "react";
import type { Job } from "@/types/jobs-types";
import type { GroupedJob } from "@/utils/groupJobs";

/**
 * Clean, primitive-facing data shape for JobEntity. Upstream shapes
 * (`Job`, `GroupedJob`) are adapted via `toJobEntityData` /
 * `groupedToJobEntityData`.
 */
export interface JobEntityLocation {
  city?: string;
  state?: string;
  country?: string;
  remote?: boolean;
  /** Stable key (falls back to `${city} ${state}`). */
  key?: string;
  /** Original Job for this location — internal use so external URLs / ids stay per-location. */
  sourceJob?: Job;
}

export interface JobEntityData {
  id: string;
  title: string;
  company: { id?: string; name: string; logoUrl?: string };
  locations: JobEntityLocation[];
  /** Original source-board posting date. */
  postedAt?: Date | string;
  /** Freshness: when the job was last seen from a source. Preferred for display. */
  lastSeenAt?: Date | string;
  description?: string;
  categoryLabel?: string;
  externalUrl?: string;
  cpc?: number;
  fitScore?: number;
  fitReason?: string;
  badgeLabel?: string;
  /** Original Job object (primary location) — internal passthrough for tracking + dialog wiring. */
  sourceJob?: Job;
}

export interface JobEntityActions {
  apply?: boolean;
  contactHiringManager?: boolean;
  viewDetails?: boolean;
  save?: boolean;
}

export interface JobEntityProps {
  job: JobEntityData;
  layout?: "card" | "row";
  density?: "comfortable" | "compact";
  actions?: JobEntityActions;
  onApply?: (job: JobEntityData) => void;
  onContactHiringManager?: (job: JobEntityData) => void;
  onViewDetails?: (job: JobEntityData) => void;
  onSave?: (job: JobEntityData) => void;
  onCardClick?: (job: JobEntityData) => void;
  trackingContext?: string;
  badge?: ReactNode;
  className?: string;
}

/* ----------------------------- Adapters ---------------------------------- */

function extractLocation(job: Job): { city: string; state: string; country?: string } {
  if (typeof job.location === "string") {
    const parts = job.location.split(",").map((s) => s.trim());
    return {
      city: job.city || parts[0] || "",
      state: job.state || parts[1] || "",
      country: job.country,
    };
  }
  if (job.location) {
    return {
      city: job.location.city,
      state: job.location.state,
      country: job.location.country || job.country,
    };
  }
  return { city: job.city || "", state: job.state || "", country: job.country };
}

export function toJobEntityData(job: Job): JobEntityData {
  const { city, state, country } = extractLocation(job);
  return {
    id: String(job.id ?? job.job_id ?? ""),
    title: job.title,
    company: {
      name: job.company,
      logoUrl: job.logo_url || job.logo,
    },
    locations: [
      {
        city,
        state,
        country,
        remote: job.remote,
        key: `${city} ${state}`.trim().toLowerCase(),
        sourceJob: job,
      },
    ],
    postedAt: job.posted_date || job.date,
    lastSeenAt: job.last_seen_at,
    description: job.description,
    categoryLabel: job.category,
    externalUrl: job.job_url || job.click_url,
    cpc: job.cpc,
    sourceJob: job,
  };
}

/**
 * Reverse of `toJobEntityData`: rebuild a `Job` from a `JobEntityData` so the
 * full `JobDetail` view can be reused (e.g. inside the Simple Search job
 * preview dialog). Prefers the `sourceJob` passthrough when present; otherwise
 * reconstructs from the entity's primitives. Some feed paths (unified-search's
 * `mapReportJobToEntity`) don't carry `sourceJob`, so the reconstruction is the
 * real path there — `description` stays as the original HTML for rich rendering.
 */
export function jobEntityDataToJob(entity: JobEntityData): Job {
  if (entity.sourceJob) return entity.sourceJob;
  const primary = entity.locations[0];
  const city = primary?.city ?? "";
  const state = primary?.state ?? "";
  const country = primary?.country ?? "";
  const toDateString = (value?: Date | string): string | undefined =>
    value instanceof Date ? value.toISOString() : value;
  return {
    id: entity.id,
    job_id: entity.id,
    title: entity.title,
    description: entity.description ?? "",
    company: entity.company.name,
    logo: entity.company.logoUrl ?? "",
    logo_url: entity.company.logoUrl,
    location: {
      loc: [city, state].filter(Boolean).join(", "),
      city,
      state,
      zip: "",
      country,
    },
    city,
    state,
    country,
    category: entity.categoryLabel ?? "",
    cpc: entity.cpc ?? 0,
    click_url: entity.externalUrl ?? "",
    job_url: entity.externalUrl,
    imp_url: null,
    posted_date: toDateString(entity.postedAt),
    last_seen_at: toDateString(entity.lastSeenAt),
    date: toDateString(entity.postedAt),
    remote: primary?.remote,
  };
}

export function groupedToJobEntityData(groupedJob: GroupedJob): JobEntityData {
  const primary = groupedJob.primaryJob;
  const { country } = extractLocation(primary);
  return {
    id: String(primary.id ?? primary.job_id ?? ""),
    title: primary.title,
    company: {
      name: primary.company,
      logoUrl: primary.logo_url || primary.logo,
    },
    locations: groupedJob.locations.map((loc) => ({
      city: loc.city,
      state: loc.state,
      country,
      remote: loc.job.remote,
      key: loc.locationKey,
      sourceJob: loc.job,
    })),
    postedAt: primary.posted_date || primary.date,
    lastSeenAt: primary.last_seen_at,
    description: primary.description,
    categoryLabel: primary.category,
    externalUrl: primary.job_url || primary.click_url,
    cpc: primary.cpc,
    sourceJob: primary,
  };
}
